import React, { useEffect, useMemo, useCallback } from "react";
import { Button, Grid } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { DECONSOLIDATION_TYPE } from "data/static/staticOptions";
import { useLazyGetDDLConsolidationForDeconsolidationQuery } from "store/services/consolidationApi";

const INITIAL_STATE = {
  account: null,
  entity: null,
  type: "",
  consolidationId: null,
  shipmentId: [],
  isActive: true,
};

const DeconsolidationForm = ({
  isBusy = null,
  deconsolidationDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity?._id ? viewEntity.entity : null,
      account: viewEntity?.entity?.account?._id ? viewEntity.entity.account : null,
      shipmentId: [],
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchConsolidationId = useWatch({ control, name: "consolidationId" });

  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();
  const [fetchConsolidations, { data: consolidations, isLoading: loadingConsolidations }] = useLazyGetDDLConsolidationForDeconsolidationQuery();

  // Get selected consolidation's shipments
  const selectedConsolidation = useMemo(() => {
    return consolidations?.find((cons) => cons?._id === _watchConsolidationId?._id);
  }, [consolidations, _watchConsolidationId]);

  // Available shipments for the dropdown
  const availableShipments = useMemo(() => {
    if (selectedConsolidation?.shipmentId) {
      return selectedConsolidation.shipmentId;
    }
    return [];
  }, [selectedConsolidation]);

  // Auto-populate shipments when consolidation is selected
  useEffect(() => {
    if (_watchConsolidationId && selectedConsolidation && !state?.shipments) {
      const allShipments = selectedConsolidation.shipmentId || [];
      setValue("shipmentId", allShipments);
    } else if (!_watchConsolidationId && !state?.shipments) {
      setValue("shipmentId", []);
    }
  }, [_watchConsolidationId, selectedConsolidation, state?.shipments, setValue]);

  // Auto-populate shipmentId from state (when coming from another page)
  useEffect(() => {
    if (state?.shipments?.length && consolidations?.length) {
      // Find shipments across all consolidations
      const allConsolidationShipments = consolidations.flatMap((cons) => cons.shipmentId || []);

      const validShipments = state.shipments
        .map((s) => {
          const shipmentId = s.shipment?.toString() || s._id?.toString();
          return allConsolidationShipments.find((opt) => opt._id?.toString() === shipmentId);
        })
        .filter(Boolean);

      if (validShipments.length > 0) {
        setValue("shipmentId", validShipments);

        // Auto-select consolidation if all shipments belong to same consolidation
        const consolidationForShipments = consolidations.find((cons) => {
          const consShipmentIds = (cons.shipmentId || []).map((s) => s._id?.toString());
          return validShipments.every((vs) => consShipmentIds.includes(vs._id?.toString()));
        });

        if (consolidationForShipments && !_watchConsolidationId) {
          setValue("consolidationId", consolidationForShipments);
        }
      }
    }
  }, [state?.shipments, consolidations, setValue, _watchConsolidationId]);

  const handleOnSubmit = useCallback((data) => {
    const payload = {
      ...data,
      entity: data?.entity?._id || null,
      account: data?.account?._id || null,
      consolidationId: data?.consolidationId?._id || null,
      shipmentId: data?.shipmentId?.map((shipment) => shipment?._id) || [],
    };
    onSubmit(payload);
  }, [onSubmit]);

  // Fetch entities when account changes
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch consolidations when entity changes
  useEffect(() => {
    if (_watchEntity?._id && !deconsolidationDetail) {
      fetchConsolidations({
        account: _watchAccount?._id,
        entity: _watchEntity._id,
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchConsolidations, deconsolidationDetail]);

  // Reset form when editing
  useEffect(() => {
    if (!deconsolidationDetail) return;

    reset({
      ...INITIAL_STATE,
      ...deconsolidationDetail,
      consolidationId: deconsolidationDetail?.consolidationId?._id
        ? deconsolidationDetail.consolidationId
        : null,
      shipmentId: deconsolidationDetail?.shipmentId || [],
      account: deconsolidationDetail?.account?._id
        ? deconsolidationDetail.account
        : null,
      entity: deconsolidationDetail?.entity?._id
        ? deconsolidationDetail.entity
        : null,
    });

    const accountId =
      deconsolidationDetail?.account?._id || viewEntity?.entity?.account?._id || null;
    if (accountId && (isAccountAdmin || isSystemAdmin)) {
      fetchEntities({ account: accountId });
    }

    const entityId =
      deconsolidationDetail?.entity?._id || viewEntity?.entity?._id || null;
    if (entityId) {
      fetchConsolidations({ account: accountId, entity: entityId });
    }
  }, [
    deconsolidationDetail,
    reset,
    fetchEntities,
    fetchConsolidations,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity,
  ]);

  // Update consolidationId when consolidations are fetched (for edit mode)
  useEffect(() => {
    if (deconsolidationDetail && consolidations) {
      const consolidationIdToMatch = deconsolidationDetail?.consolidationId?._id || null;
      if (!consolidationIdToMatch) {
        setValue("consolidationId", null);
        return;
      }

      const matchingConsolidation = consolidations.find(
        (cons) => cons._id === consolidationIdToMatch
      );

      if (matchingConsolidation) {
        setValue("consolidationId", matchingConsolidation);
      } else {
        setValue("consolidationId", {
          _id: consolidationIdToMatch,
          consolidationId: `Unknown (${consolidationIdToMatch})`,
        });
      }
    }
  }, [deconsolidationDetail, consolidations, setValue]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("consolidationId", null);
                      setValue("shipmentId", []);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null || value === ""
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin || loadingAccounts}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Entity"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("consolidationId", null);
                      setValue("shipmentId", []);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null || value === ""
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    disabled={loadingEntities}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          {!deconsolidationDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="consolidationId"
                control={control}
                rules={{ required: "Please select a consolidation." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Consolidation"
                    required
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      // Clear shipments when consolidation changes
                      if (!data) {
                        setValue("shipmentId", []);
                      }
                    }}
                    getOptionLabel={(option) => option?.consolidationId ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null || value === ""
                    }
                    options={consolidations || []}
                    loading={loadingConsolidations}
                    loadingText="Loading consolidations..."
                    helperText={errors?.consolidationId?.message}
                    error={!!errors?.consolidationId}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select at least one Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  multiple
                  fullWidth
                  label="Shipment ID"
                  required
                  value={value || []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.IsReturning
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={availableShipments}
                  loading={loadingConsolidations}
                  loadingText="Loading Shipments..."
                  helperText={
                    errors?.shipmentId?.message ||
                    (!_watchConsolidationId && !state?.shipments
                      ? "Select a consolidation first"
                      : "")
                  }
                  error={!!errors?.shipmentId}
                  disabled={
                    !!state?.shipments ||
                    loadingConsolidations ||
                    !!deconsolidationDetail ||
                    (!_watchConsolidationId && !state?.shipments)
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Please select a type." }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Type"
                  options={DECONSOLIDATION_TYPE}
                  helperText={errors?.type?.message}
                  error={!!errors?.type}
                />
              )}
            />
          </Grid>
          {deconsolidationDetail && (
            <Grid item md={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isBusy}
        >
          {deconsolidationDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default DeconsolidationForm;